/**
 * Admin Panel - PBAC Management API
 *
 * Zod schemas for Policy-Based Access Control (PBAC) system.
 * Comprehensive admin panel endpoints for managing policies, users, resources, and audit logs.
 *
 * Base path: /api/admin-panel
 *
 * Architecture: Three-sword style (TypeScript + NestJS + Prisma)
 * - Type-safe schemas for all endpoints
 * - Request validation (params, body, path vars)
 * - Response standardization with ApiSuccessSchema
 */

import { z } from "zod";

import type {
  AdminAuditAction,
  AdminAuditLogEntry,
  AdminAuditLogsResponse,
  AdminPermissionAction,
  AdminPermissionCheckResult,
  AdminPolicy,
  AdminPolicyRule,
  AdminResource,
  AdminResourceTree,
  AdminResourceType,
  AdminStatusResponse,
  AdminUserOverride,
  AdminUserWithPermissions
} from "../../../shared/admin/permission.types";
import type { TypesMatchExactly } from "../../../shared/core/type-assertions";
import { ApiSuccessSchema } from "../../common/response.schema";

// ═══════════════════════════════════════════════════════════════
// ENUMS & CONSTANTS
// ═══════════════════════════════════════════════════════════════

/**
 * Permission action enum (R/W/X)
 */
export const AdminPermissionActionSchema = z.enum(["read", "write", "delete"]);

/**
 * Resource type enum
 */
export const AdminResourceTypeSchema = z.enum(["NAV", "BLOCK", "ACTION"]);

/**
 * Audit action enum
 */
export const AdminAuditActionSchema = z.enum(["READ", "WRITE", "DELETE"]);

/**
 * Audit log time range for stats
 */
export const AuditTimeRangeSchema = z.enum(["24h", "7d", "30d", "90d"]);

// ═══════════════════════════════════════════════════════════════
// SHARED SCHEMAS - Building blocks
// ═══════════════════════════════════════════════════════════════

/**
 * Identifier for an admin PBAC entity (policy, resource, override, user) as it
 * appears in API *responses*.
 *
 * These ids are server-issued and trusted — they are NOT user input at this
 * boundary. They include two shapes that strict UUID validation rejects:
 *   1. Seeded system sentinels that are UUID-shaped but not RFC 9562-valid,
 *      e.g. the ROOT policy id `00000000-0000-0000-0000-000000000001` (version
 *      nibble `0`, and not the nil/max special case). Zod v3's `.uuid()`
 *      accepted these; Zod v4 tightened `.uuid()` to RFC 9562 and rejects them,
 *      which made the entire `GetMyStatus` response fail to parse for any admin
 *      on a system policy — locking them out of the admin panel.
 *   2. Empty strings, e.g. override `id: ""` emitted by the controller for
 *      overrides whose id is not surfaced.
 *
 * Validate as a plain string so trusted server ids round-trip intact. Request
 * inputs (path vars / bodies) keep their stricter validation.
 */
const AdminEntityIdSchema = z.string();

/**
 * Policy rule schema (resource + permission bitmask)
 */
export const AdminPolicyRuleSchema = z.object({
  resource: z.string().min(1, "Resource path required"),
  permission: z.number().int().min(0).max(7, "Permission must be 0-7 (RWX bitmask)")
});

/**
 * User override schema
 */
export const AdminUserOverrideSchema = z.object({
  id: AdminEntityIdSchema,
  resource: z.string(),
  permission: z.number().int().min(0).max(7),
  reason: z.string().nullable(),
  expiresAt: z.string().datetime().nullable()
});

/**
 * Admin resource schema
 */
export const AdminResourceSchema = z.object({
  id: AdminEntityIdSchema,
  path: z.string(),
  name: z.string(),
  description: z.string().nullable(),
  category: z.string().nullable(),
  resourceType: AdminResourceTypeSchema,
  parentPath: z.string().nullable(),
  sortOrder: z.number().int()
});

/**
 * Admin resource tree node (recursive)
 */
export const AdminResourceTreeSchema: z.ZodType<AdminResourceTree> = z.lazy(() =>
  z.object({
    id: AdminEntityIdSchema,
    path: z.string(),
    name: z.string(),
    description: z.string().nullable(),
    category: z.string().nullable(),
    resourceType: AdminResourceTypeSchema,
    sortOrder: z.number().int(),
    children: z.array(AdminResourceTreeSchema)
  })
);

/**
 * Admin policy schema
 */
export const AdminPolicySchema = z.object({
  id: AdminEntityIdSchema,
  name: z.string(),
  description: z.string().nullable(),
  isSystem: z.boolean(),
  isEditable: z.boolean(),
  rules: z.array(AdminPolicyRuleSchema),
  userCount: z.number().int().optional(),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime()
});

/**
 * `AdminPolicy` and the schema above describe one wire shape, and nothing
 * compared them — same seam as `ADMIN_USER_WITH_PERMISSIONS_MATCHES_SCHEMA`
 * below. `AdminResourceTree` needs no equivalent: `AdminResourceTreeSchema` is
 * declared `z.ZodType<AdminResourceTree>`, so it is already bound to the
 * interface by construction and an assertion there would be tautological.
 */
export const ADMIN_POLICY_MATCHES_SCHEMA: TypesMatchExactly<
  AdminPolicy,
  z.infer<typeof AdminPolicySchema>
> = true;

/** Same, for the flat resource shape `ListResources` returns. */
export const ADMIN_RESOURCE_MATCHES_SCHEMA: TypesMatchExactly<
  AdminResource,
  z.infer<typeof AdminResourceSchema>
> = true;

/**
 * Policy summary (for user object)
 */
export const AdminPolicySummarySchema = z.object({
  id: AdminEntityIdSchema,
  name: z.string(),
  isSystem: z.boolean(),
  isEditable: z.boolean()
});

/**
 * Admin user with permissions
 */
export const AdminUserWithPermissionsSchema = z.object({
  id: AdminEntityIdSchema,
  userId: z.string(), // Clerk user ID (not UUID - format: user_xxx)
  email: z.string().email(),
  firstName: z.string().optional(),
  lastName: z.string().optional(),
  policy: AdminPolicySummarySchema,
  overrides: z.array(AdminUserOverrideSchema),
  // Raw policy-rule grants (resource → permission). Resolved client-side against
  // the active `overrides` via `resolveAdminPermission` — never pre-resolved here.
  policyGrants: z.record(z.string(), z.number()).optional()
});

/**
 * The hand-written `AdminUserWithPermissions` and the schema above are two
 * descriptions of one wire shape, and nothing compared them. Six admin routes
 * declare this schema as their `Response` while every producer behind them —
 * `ListAdminUsersUseCase`, `GetAdminUserUseCase`, and the four write use cases —
 * is typed as the interface. Typing a producer as the interface is only
 * equivalent to typing it as the contract while the two agree; this is what
 * makes that true by construction rather than by inspection.
 *
 * It is stated here because this file already imports both sides. The reverse —
 * `permission.types.ts` inferring from the schema — would have the shared type
 * layer depend on the API contract layer, which is a cycle and the wrong
 * direction.
 */
export const ADMIN_USER_WITH_PERMISSIONS_MATCHES_SCHEMA: TypesMatchExactly<
  AdminUserWithPermissions,
  z.infer<typeof AdminUserWithPermissionsSchema>
> = true;

/**
 * Admin status response schema
 */
export const AdminStatusResponseSchema = z.object({
  isAdmin: z.boolean(),
  user: AdminUserWithPermissionsSchema.nullable(),
  resources: z.array(AdminResourceSchema)
});

/**
 * User search result (for search endpoint)
 */
export const UserSearchResultSchema = z.object({
  userId: z.string(), // Clerk user ID (not UUID - format: user_xxx)
  email: z.string().email(),
  firstName: z.string().optional(),
  lastName: z.string().optional()
});

/**
 * Audit log entry schema
 */
export const AdminAuditLogEntrySchema = z.object({
  id: AdminEntityIdSchema,
  adminUserId: z.string().uuid().nullable(),
  userEmail: z.string().email().nullable(),
  action: AdminAuditActionSchema,
  resource: z.string(),
  // VARCHAR(255) at the DB layer (see AdminAuditInterceptor comment),
  // not a UUID. Custom `extractResourceId` callers on admin endpoints
  // legitimately write Clerk ids (`org_xxx`, `user_xxx`), Twilio SIDs,
  // and other opaque strings here. Tightening to `.uuid()` makes the
  // viewer crash on any row an extractor produced with a non-UUID id.
  resourceId: z.string().nullable(),
  meta: z.record(z.string(), z.unknown()),
  ipAddress: z.string().nullable(),
  userAgent: z.string().nullable(),
  createdAt: z.string().datetime()
});

/**
 * Audit logs paginated response schema
 */
export const AdminAuditLogsResponseSchema = z.object({
  logs: z.array(AdminAuditLogEntrySchema),
  total: z.number().int(),
  page: z.number().int(),
  pageSize: z.number().int(),
  totalPages: z.number().int()
});

// ═══════════════════════════════════════════════════════════════
// REQUEST SCHEMAS - Path vars, params, and body
// ═══════════════════════════════════════════════════════════════

// --- Admin Status & Permissions ---

// --- Policies ---

/**
 * Path vars for policy endpoints
 *
 * The key is `id` because that is what `AdminPanelPoliciesController` routes on
 * (`@Get(":id")`). A path variable's NAME is part of the route identity, not a
 * label: the admin tool catalog joins reflected routes to contracts on the
 * literal `METHOD path`, and fills `:params` from the tool's arguments by name.
 * A contract that said `:policyId` described a route the server does not serve —
 * it produced no tool at all, and had it matched loosely it would have produced
 * a tool that substituted an empty string for `:id`.
 */
/**
 * A policy id, wherever one appears — path var or body field.
 *
 * **Deliberately not `.uuid()`.** Seeded system policies use sentinel ids that
 * are UUID-shaped but not RFC 9562-valid: ROOT is
 * `00000000-0000-0000-0000-000000000001`, whose version nibble is `0` and which
 * is neither the nil nor the max special case. Zod v3 accepted those; v4
 * tightened it and rejects them.
 *
 * That has already cost one outage. When `AdminStatusResponseSchema` used
 * `.uuid()`, the whole GetMyStatus response failed to parse and every
 * system-policy admin was locked out of the panel — see `zadmin-panel.test.ts`.
 * It then nearly cost a second one: adding `.parse()` to the admin-user
 * handlers turned `policyId: z.string().uuid()` from an unread annotation into
 * a live gate, which would have 400'd every attempt to grant or reassign ROOT.
 *
 * The lesson is that the decision has to live in ONE place. Three fields held
 * three independent copies of it and only the path var got it right, because
 * only the path var had been bitten. Server-side lookup scopes by id either
 * way, so non-empty is the honest guard.
 */
export const AdminPolicyIdSchema = z.string().min(1, "Invalid policy ID");

export const PolicyPathVarsSchema = z.object({
  id: AdminPolicyIdSchema
});

/**
 * Create policy request body
 */
export const CreatePolicyBodySchema = z.object({
  // `.trim()` BEFORE `.min(1)`, and the order is the whole point: `.min(1)`
  // alone accepts "   ", which `dto.name.trim()` then stores as an empty policy
  // name. Zod's `.trim()` is a transform, so the handler receives the trimmed
  // value and no caller has to remember to trim again.
  name: z.string().trim().min(1, "Policy name required").max(100),
  description: z.string().max(500).optional(),
  rules: z.array(AdminPolicyRuleSchema).default([])
});

/**
 * Update policy request body
 */
export const UpdatePolicyBodySchema = z.object({
  name: z.string().trim().min(1).max(100).optional(),
  description: z.string().max(500).nullable().optional(),
  rules: z.array(AdminPolicyRuleSchema).optional()
});

// --- Admin Users ---

/**
 * Path vars for admin-user endpoints
 *
 * The key is `id` because that is what `AdminPanelUsersController` routes on
 * (`@Get(":id")`) — a path variable's name is part of the route identity, not a
 * label. See `PolicyPathVarsSchema` for why a mismatch here costs the admin
 * agent the whole capability.
 *
 * `.uuid()` is correct: this is the `NxAdminUser` row id, not the Clerk id the
 * row points at. The two are different values and only one of them is a UUID —
 * see `CreateAdminUserBodySchema.userId`.
 */
export const UserPathVarsSchema = z.object({
  id: z.string().uuid("Invalid admin user ID")
});

/**
 * Query params for user search
 */
export const UserSearchParamsSchema = z.object({
  q: z.string().min(1, "Search query required")
});

/**
 * Create admin user request body
 *
 * ⚠️ `userId` is a **Clerk id** (`user_2abc…`), not a UUID. `NxAdminUser.userId`
 * is declared `String @unique // References User.id (Clerk ID)` and FKs to
 * `User.id`; `GET /users/search` returns Clerk ids, and that is what this
 * endpoint is fed. `AdminUserWithPermissionsSchema.userId` says so on the
 * response side — this schema used to contradict it with `.uuid()`.
 *
 * It never surfaced through the panel, because the controller takes a raw DTO
 * and never parses this schema. It surfaced through the admin agent: the tool
 * catalog projects the contract to JSON Schema, so `.uuid()` became
 * `format: "uuid"` plus an RFC 9562 `pattern` in the tool the model is offered
 * — instructing it to invent a UUID for a field whose only valid values are
 * Clerk ids. A contract that no server parses is still read by something.
 */
export const CreateAdminUserBodySchema = z.object({
  userId: z.string().min(1, "User ID required"),
  policyId: AdminPolicyIdSchema
});

/**
 * Update admin user request body
 */
export const UpdateAdminUserBodySchema = z.object({
  policyId: AdminPolicyIdSchema
});

// --- User Overrides ---

/**
 * Path vars for override endpoints
 *
 * `id` is the `NxAdminUser` row id — the handler passes it straight through as
 * `adminUserId`. Named for the route segment the controller serves, as above.
 */
export const OverridePathVarsSchema = z.object({
  id: z.string().uuid("Invalid admin user ID"),
  overrideId: z.string().uuid("Invalid override ID")
});

/**
 * Create user override request body
 */
export const CreateUserOverrideBodySchema = z.object({
  resource: z.string().min(1, "Resource path required"),
  permission: z.number().int().min(0).max(7, "Permission must be 0-7 (RWX bitmask)"),
  reason: z.string().max(500).optional(),
  expiresAt: z.string().datetime().nullable().optional()
});

// --- Audit Logs ---
/**
 * Query params for audit logs list
 */
export const AuditLogsParamsSchema = z.object({
  limit: z.coerce.number().int().min(1).max(250).optional().default(100),
  offset: z.coerce.number().int().min(0).optional().default(0),
  adminUserId: z.string().optional(),
  resource: z.string().optional(),
  action: z.enum(["READ", "WRITE", "DELETE"]).optional(),
  startDate: z.string().optional(),
  endDate: z.string().optional(),
  excludeReads: z
    .union([z.boolean(), z.string()])
    .transform((val) => {
      if (typeof val === "boolean") return val;
      if (val === "true") return true;
      if (val === "false") return false;
      return false;
    })
    .optional()
});

/**
 * Query params for audit log stats
 */
export const AuditStatsParamsSchema = z.object({
  range: AuditTimeRangeSchema.optional().default("24h")
});

/**
 * Path vars for a single audit log entry
 */
export const AuditLogPathVarsSchema = z.object({
  id: z.string().uuid("Invalid audit log ID")
});

/**
 * Path vars for one admin's activity — `@Get("user/:userId")`
 *
 * ⚠️ `userId` is the segment the controller routes on, but the value is the
 * `NxAdminUser` ROW id: the handler passes it straight to
 * `AdminAuditService.getUserActivity`, which queries `where: { adminUserId }`,
 * and that column is `uuid`. It is NOT the Clerk id that `NxAdminUser.userId`
 * holds. Two different values, one name — hence `.uuid()`.
 */
export const AuditUserActivityPathVarsSchema = z.object({
  userId: z.string().uuid("Invalid admin user ID")
});

/**
 * Query params for one admin's activity. The server defaults `limit` to 50.
 */
export const AuditUserActivityParamsSchema = z.object({
  limit: z.coerce.number().int().min(1).max(250).optional().default(50)
});

/**
 * Path vars for a resource's activity — `@Get("resource/:resource")`
 *
 * Matched as a PREFIX server-side (`startsWith`, with a trailing `*` stripped),
 * so `admin:vault` returns `admin:vault:pool` too.
 */
export const AuditResourceActivityPathVarsSchema = z.object({
  resource: z.string().min(1, "Resource required")
});

/**
 * Query params for a resource's activity. The server defaults `limit` to 50.
 */
export const AuditResourceActivityParamsSchema = z.object({
  resourceId: z.string().optional(),
  limit: z.coerce.number().int().min(1).max(250).optional().default(50)
});

/**
 * Activity response — a flat list, with none of the pagination the main audit
 * list carries, because these routes take a `limit` and return exactly that.
 */
export const AdminAuditActivityResponseSchema = z.object({
  logs: z.array(AdminAuditLogEntrySchema)
});

// ═══════════════════════════════════════════════════════════════
// RESPONSE SCHEMAS - Specialized responses
// ═══════════════════════════════════════════════════════════════

/**
 * Resource seed response
 */
/**
 * What `POST /admin-panel/resources/seed` actually returns.
 *
 * Declared `{ seeded, skipped }` while the handler has always returned
 * `{ status, message, total }` — neither field existed on the wire. Nothing
 * failed, because no response validation runs and nothing read them; the
 * contract simply described an endpoint that does not exist. Typing the audit
 * extractor from it is what surfaced the difference.
 */
export const ResourceSeedResponseSchema = z.object({
  status: z.literal("success"),
  message: z.string(),
  total: z.number().int()
});

/**
 * Audit stats response
 */
export const AuditStatsResponseSchema = z.object({
  totalActions: z.number().int(),
  byAction: z.record(z.string(), z.number().int()),
  byResource: z.array(
    z.object({
      resource: z.string(),
      count: z.number().int()
    })
  ),
  byUser: z.array(
    z.object({
      email: z.string(),
      count: z.number().int()
    })
  )
});

/**
 * Void response (for DELETE operations)
 */
export const VoidResponseSchema = z.object({});

/**
 * What the admin-panel delete handlers actually return.
 *
 * `DeletePolicy` and `DeleteUser` were declared `VoidResponseSchema` — `z.object({})`,
 * which is non-strict and so validated a `{ success, message }` body without
 * complaint. Nothing failed; the contract simply did not describe the endpoint,
 * and anything typed from it (an audit extractor reading `data.message`, a future
 * frontend consumer) was reasoning about a shape the handler does not return.
 */
export const AdminMutationOutcomeSchema = z.object({
  success: z.boolean(),
  message: z.string()
});

// ═══════════════════════════════════════════════════════════════
// API SCHEMA OBJECT - Export for TApi generation
// ═══════════════════════════════════════════════════════════════

export const ZAdminPanel = {
  // ─────────────────────────────────────────────────────────────
  // Admin Status & Permissions
  // ─────────────────────────────────────────────────────────────

  /**
   * Get current user's admin status
   * GET /api/admin-panel/me
   */
  GetMyStatus: {
    method: "GET" as const,
    path: "/api/admin-panel/me",
    Response: ApiSuccessSchema(AdminStatusResponseSchema)
  },

  // ─────────────────────────────────────────────────────────────
  // Policies
  // ─────────────────────────────────────────────────────────────

  /**
   * List all policies
   * GET /api/admin-panel/policies
   */
  ListPolicies: {
    method: "GET" as const,
    path: "/api/admin-panel/policies",
    Response: ApiSuccessSchema(z.array(AdminPolicySchema))
  },

  /**
   * Get policy by ID
   * GET /api/admin-panel/policies/:id
   */
  GetPolicy: {
    method: "GET" as const,
    path: "/api/admin-panel/policies/:id",
    PathVars: PolicyPathVarsSchema,
    Response: ApiSuccessSchema(AdminPolicySchema)
  },

  /**
   * Create new policy
   * POST /api/admin-panel/policies
   */
  CreatePolicy: {
    method: "POST" as const,
    path: "/api/admin-panel/policies",
    Body: CreatePolicyBodySchema,
    Response: ApiSuccessSchema(AdminPolicySchema)
  },

  /**
   * Update existing policy
   * PATCH /api/admin-panel/policies/:id
   */
  UpdatePolicy: {
    method: "PATCH" as const,
    path: "/api/admin-panel/policies/:id",
    PathVars: PolicyPathVarsSchema,
    Body: UpdatePolicyBodySchema,
    Response: ApiSuccessSchema(AdminPolicySchema)
  },

  /**
   * Delete policy
   * DELETE /api/admin-panel/policies/:id
   */
  DeletePolicy: {
    method: "DELETE" as const,
    path: "/api/admin-panel/policies/:id",
    PathVars: PolicyPathVarsSchema,
    Response: ApiSuccessSchema(AdminMutationOutcomeSchema)
  },

  // ─────────────────────────────────────────────────────────────
  // Admin Users
  // ─────────────────────────────────────────────────────────────

  /**
   * List all admin users
   * GET /api/admin-panel/users
   */
  ListUsers: {
    method: "GET" as const,
    path: "/api/admin-panel/users",
    Response: ApiSuccessSchema(z.array(AdminUserWithPermissionsSchema))
  },

  /**
   * Search users (for adding new admins)
   * GET /api/admin-panel/users/search?q=email
   */
  SearchUsers: {
    method: "GET" as const,
    path: "/api/admin-panel/users/search",
    Params: UserSearchParamsSchema,
    Response: ApiSuccessSchema(z.array(UserSearchResultSchema))
  },

  /**
   * Get admin user by ID
   * GET /api/admin-panel/users/:id
   */
  GetUser: {
    method: "GET" as const,
    path: "/api/admin-panel/users/:id",
    PathVars: UserPathVarsSchema,
    Response: ApiSuccessSchema(AdminUserWithPermissionsSchema)
  },

  /**
   * Create admin user (grant admin access)
   * POST /api/admin-panel/users
   */
  CreateUser: {
    method: "POST" as const,
    path: "/api/admin-panel/users",
    Body: CreateAdminUserBodySchema,
    Response: ApiSuccessSchema(AdminUserWithPermissionsSchema)
  },

  /**
   * Update admin user (change policy)
   * PATCH /api/admin-panel/users/:id
   */
  UpdateUser: {
    method: "PATCH" as const,
    path: "/api/admin-panel/users/:id",
    PathVars: UserPathVarsSchema,
    Body: UpdateAdminUserBodySchema,
    Response: ApiSuccessSchema(AdminUserWithPermissionsSchema)
  },

  /**
   * Delete admin user (revoke admin access)
   * DELETE /api/admin-panel/users/:id
   */
  DeleteUser: {
    method: "DELETE" as const,
    path: "/api/admin-panel/users/:id",
    PathVars: UserPathVarsSchema,
    Response: ApiSuccessSchema(AdminMutationOutcomeSchema)
  },

  // ─────────────────────────────────────────────────────────────
  // User Overrides
  // ─────────────────────────────────────────────────────────────

  /**
   * Create user override (grant/deny specific permission)
   * POST /api/admin-panel/users/:id/overrides
   */
  CreateOverride: {
    method: "POST" as const,
    path: "/api/admin-panel/users/:id/overrides",
    PathVars: UserPathVarsSchema,
    Body: CreateUserOverrideBodySchema,
    Response: ApiSuccessSchema(AdminUserWithPermissionsSchema)
  },

  /**
   * Delete user override
   * DELETE /api/admin-panel/users/:id/overrides/:overrideId
   */
  DeleteOverride: {
    method: "DELETE" as const,
    path: "/api/admin-panel/users/:id/overrides/:overrideId",
    PathVars: OverridePathVarsSchema,
    Response: ApiSuccessSchema(AdminUserWithPermissionsSchema)
  },

  // ─────────────────────────────────────────────────────────────
  // Resources
  // ─────────────────────────────────────────────────────────────

  /**
   * List all resources (flat)
   * GET /api/admin-panel/resources
   */
  ListResources: {
    method: "GET" as const,
    path: "/api/admin-panel/resources",
    Response: ApiSuccessSchema(z.array(AdminResourceSchema))
  },

  /**
   * Get resource tree (hierarchical)
   * GET /api/admin-panel/resources/tree
   */
  GetResourceTree: {
    method: "GET" as const,
    path: "/api/admin-panel/resources/tree",
    Response: ApiSuccessSchema(z.array(AdminResourceTreeSchema))
  },

  /**
   * Seed default resources (dev/migration tool)
   * POST /api/admin-panel/resources/seed
   */
  SeedResources: {
    method: "POST" as const,
    path: "/api/admin-panel/resources/seed",
    Response: ApiSuccessSchema(ResourceSeedResponseSchema)
  },

  // ─────────────────────────────────────────────────────────────
  // Audit Logs
  // ─────────────────────────────────────────────────────────────

  /**
   * List audit logs (paginated)
   * GET /api/admin-panel/audit?limit=100&offset=0
   */
  ListAuditLogs: {
    method: "GET" as const,
    path: "/api/admin-panel/audit",
    Params: AuditLogsParamsSchema,
    Response: ApiSuccessSchema(AdminAuditLogsResponseSchema)
  },

  /**
   * Get audit log statistics
   * GET /api/admin-panel/audit/stats?range=24h
   */
  GetAuditStats: {
    method: "GET" as const,
    path: "/api/admin-panel/audit/stats",
    Params: AuditStatsParamsSchema,
    Response: ApiSuccessSchema(AuditStatsResponseSchema)
  },

  /**
   * Get a single audit log entry
   * GET /api/admin-panel/audit/:id
   */
  GetAuditLog: {
    method: "GET" as const,
    path: "/api/admin-panel/audit/:id",
    PathVars: AuditLogPathVarsSchema,
    Response: ApiSuccessSchema(AdminAuditLogEntrySchema)
  },

  /**
   * Get one admin's recent activity
   * GET /api/admin-panel/audit/user/:userId?limit=50
   */
  GetUserActivity: {
    method: "GET" as const,
    path: "/api/admin-panel/audit/user/:userId",
    PathVars: AuditUserActivityPathVarsSchema,
    Params: AuditUserActivityParamsSchema,
    Response: ApiSuccessSchema(AdminAuditActivityResponseSchema)
  },

  /**
   * Get recent activity against a resource (prefix-matched)
   * GET /api/admin-panel/audit/resource/:resource?resourceId=…&limit=50
   */
  GetResourceActivity: {
    method: "GET" as const,
    path: "/api/admin-panel/audit/resource/:resource",
    PathVars: AuditResourceActivityPathVarsSchema,
    Params: AuditResourceActivityParamsSchema,
    Response: ApiSuccessSchema(AdminAuditActivityResponseSchema)
  }
} as const;

// ═══════════════════════════════════════════════════════════════
// EXPORTED TYPES - For convenience
// ═══════════════════════════════════════════════════════════════

// Request types
export type CreatePolicyBody = z.infer<typeof CreatePolicyBodySchema>;
export type UpdatePolicyBody = z.infer<typeof UpdatePolicyBodySchema>;
export type CreateAdminUserBody = z.infer<typeof CreateAdminUserBodySchema>;
export type UpdateAdminUserBody = z.infer<typeof UpdateAdminUserBodySchema>;
export type CreateUserOverrideBody = z.infer<typeof CreateUserOverrideBodySchema>;
export type AuditLogsParams = z.infer<typeof AuditLogsParamsSchema>;
export type AuditStatsParams = z.infer<typeof AuditStatsParamsSchema>;
export type UserSearchParams = z.infer<typeof UserSearchParamsSchema>;

// Response types
export type UserSearchResult = z.infer<typeof UserSearchResultSchema>;
export type ResourceSeedResponse = z.infer<typeof ResourceSeedResponseSchema>;
export type AuditStatsResponse = z.infer<typeof AuditStatsResponseSchema>;

// Re-export shared types for convenience
export type {
  AdminAuditAction,
  AdminAuditLogEntry,
  AdminAuditLogsResponse,
  AdminPermissionAction,
  AdminPermissionCheckResult,
  AdminPolicy,
  AdminPolicyRule,
  AdminResource,
  AdminResourceTree,
  AdminResourceType,
  AdminStatusResponse,
  AdminUserOverride,
  AdminUserWithPermissions
};
